import { computed } from 'vue';
import type { UiStyling } from '../types/ui-styling';
import ColorUtilsHelper from '../helpers/color-utils.helper';
import type { EntityTheme } from '../types/theme';
import { BackgroundType } from '../types/enums/background-type';

export function useUIStyling(theme: Omit<EntityTheme, 'id' | 'entityId'> | null) {
  if (!theme) {
    return {
      uiStyling: computed<UiStyling | undefined>(() => undefined),
    };
  }

  const navigationItemsBackgroundColor = computed(() =>
    ColorUtilsHelper.lightenColor(theme.color, 85)
  );

  const buttonHoverColor = computed(() => ColorUtilsHelper.calculateHoverColor(theme.color));

  const navigationItemsTextColor = computed(() =>
    ColorUtilsHelper.getAccessibleTextColor(navigationItemsBackgroundColor.value)
  );

  const backgroundImage = computed(() => {
    if ('backgroundType' in theme && theme.backgroundType === BackgroundType.Image) {
      return `url(${theme.backgroundImage})`;
    }

    return undefined;
  });

  const overlayColor = computed(() =>
    ColorUtilsHelper.calculateOverlayColor(theme.backgroundGradientLightness)
  );

  const backgroundGradient = computed(() => {
    switch (theme.backgroundType) {
      case BackgroundType.GradientSolid:
        return ColorUtilsHelper.calculateSolidLinearGradientWithOverlay(
          theme.backgroundGradientColorOne,
          overlayColor.value
        );
      case BackgroundType.GradientLinear:
        return ColorUtilsHelper.calculateLinearGradientWithOverlay(
          ColorUtilsHelper.calculateLinearGradient(
            theme.backgroundGradientColorOne,
            theme.backgroundGradientColorTwo
          ),
          overlayColor.value
        );
      case BackgroundType.GradientRadial:
        return ColorUtilsHelper.calculateRadialGradientWithOverlay(
          ColorUtilsHelper.calculateRadialGradient(
            theme.backgroundGradientColorOne,
            theme.backgroundGradientColorTwo
          ),
          overlayColor.value
        );
      case BackgroundType.GradientConic:
        return ColorUtilsHelper.calculateConicGradientWithOverlay(
          ColorUtilsHelper.calculateConicGradient(
            theme.backgroundGradientColorOne,
            theme.backgroundGradientColorTwo
          ),
          overlayColor.value
        );
      default:
        return undefined;
    }
  });

  const background = computed(() => {
    if (theme.backgroundType === BackgroundType.Image) {
      return backgroundImage.value;
    }

    return backgroundGradient.value;
  });

  const uiStyling = computed<UiStyling>(() => ({
    color: theme.color,
    buttonBackgroundColor: theme.color,
    buttonHoverBackgroundColor: buttonHoverColor.value,
    buttonTextColor: theme.buttonTextColor,
    navigationItemsBackgroundColor: navigationItemsBackgroundColor.value,
    navigationItemsTextColor: navigationItemsTextColor.value,
    background: background.value,
  }));

  return {
    uiStyling,
  };
}
